/**
 * Read-only banner shown when a non-owner opens someone else's session
 * in a shared space. Replaces the composer entirely — non-owners can't
 * write at all until they claim. Claim button is hidden when the
 * session is mid-stream or waiting on the other user's HITL reply
 * (`claim_blocked_reason`).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  claimSession,
  sessionMetaQueryOptions,
  type ClaimSessionError,
} from "../queries/session-queries";
export function SessionOwnerBanner({ sessionId }: { sessionId: string }) {
  const qc = useQueryClient();
  const { data: meta } = useQuery({
    ...sessionMetaQueryOptions(sessionId),
    // While we're spectators, the local lock state lives on a different
    // pod — poll so the «Возьмите на себя» button activates a few seconds
    // after the other user finishes streaming.
    refetchInterval: 5_000,
  });

  const claim = useMutation({
    mutationFn: () => claimSession(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions", sessionId, "meta"] });
      qc.invalidateQueries({ queryKey: ["sessions", sessionId, "owners"] });
      qc.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  if (!meta || meta.is_owner) return null;

  const ownerLabel = meta.owner?.name || meta.owner?.email || "другой участник";
  const reason = (claim.error as ClaimSessionError | undefined)?.code ?? meta.claim_blocked_reason;
  const reasonText =
    reason === "generation_in_progress"
      ? "Сейчас идёт генерация ответа. Возьмите сессию, как только она завершится."
      : reason === "awaiting_user_input"
        ? "Сессия ждёт ответа на интерактивный инструмент — пока её взять нельзя."
        : null;

  return (
    <div className="mx-auto w-full max-w-3xl rounded-2xl border border-black/10 bg-white px-5 py-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wide text-black/40">
            Только чтение
          </div>
          <div className="truncate text-sm text-black/80">
            Ответственный — <span className="font-medium">{ownerLabel}</span>
          </div>
          {reasonText && (
            <div className="mt-1 text-xs text-black/55">{reasonText}</div>
          )}
          {claim.error && !reason && (
            <div className="mt-1 text-xs text-red-600">
              {(claim.error as Error).message}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => claim.mutate()}
          disabled={!meta.claimable || claim.isPending}
          className="rounded-full bg-black px-5 py-2 text-sm text-white hover:bg-black/85 disabled:bg-black/30 disabled:cursor-not-allowed"
        >
          {claim.isPending ? "Беру…" : "Взять на себя"}
        </button>
      </div>
    </div>
  );
}
